import React, { useEffect, useState, useCallback } from "react";
import { Card, Badge, Table, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { getUserId } from "../services/authorize";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

// "ชั้นมัธยมศึกษาปีที่ 1/2" → "ม.1/2"
const formatRegularClass = (val) => {
  if (!val) return null;
  const [cls, room] = String(val).split('/');
  const level = cls.replace(/\D/g, '');
  return level ? `ม. ${level}${room ? '/' + room : ''}` : val;
};

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const circleStyle = { width: 28, height: 28, borderRadius: '50%', flexShrink: 0 };
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: 34, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default function IslamicAdvisorStudents() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [room, setRoom] = useState(null);
  const [students, setStudents] = useState([]);

  // รายชื่อนักเรียนร่วมหอ — ใช้ endpoint เดียวกับภาคสามัญ (requireLogin อย่างเดียว
  // ไม่ผูกกับตำแหน่ง) เพราะทะเบียนหอพักไม่ได้แยกภาคสามัญ/ศาสนาอยู่แล้ว
  const [dormModal, setDormModal] = useState(null);
  const [dormResidents, setDormResidents] = useState([]);
  const [dormLoaded, setDormLoaded] = useState(false);

  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback((yr_id) => {
    if (!yr_id) return;
    const t_id = getUserId();
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-advisor/myRoom/${t_id}/${yr_id}`)
      .then(res => {
        setRoom(res.data);
        return axiosInstance.get(`${process.env.REACT_APP_API}/islamic-advisor/students/${res.data.ir_id}`);
      })
      .then(res => setStudents(res.data))
      .catch(() => { setRoom(null); setStudents([]); })
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { fetchData(yearId); }, [yearId, fetchData]);

  // ไม่ใช้ setLoading ของหน้า — overlay เต็มจอบังโมดัลที่เพิ่งเปิด
  // สถานะกำลังโหลดบอกในตัวโมดัลเองผ่าน dormLoaded
  const openDormModal = (s) => {
    setDormModal({
      dorm_id: s.dorm_id, dorm_name: s.dorm_name, dorm_code: s.dorm_code,
      village_name: s.village_name, st_id: s.st_id,
    });
    setDormResidents([]);
    setDormLoaded(false);
    axiosInstance.get(`${process.env.REACT_APP_API}/getDormResidents/${s.dorm_id}`)
      .then(res => setDormResidents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDormResidents([]))
      .finally(() => setDormLoaded(true));
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="main-title mb-0">{t.advisorStudents.pageTitle}</h4>
          {room && (
            <p className="text-secondary mb-0 small">
              {t.common.classNo(room.ic_class, room.ir_no)}&nbsp;&nbsp;
              <Badge bg="info">{room.is_programCode}</Badge>
              &nbsp;
              <Badge bg={room.ir_gender === 'm' ? 'primary' : 'danger'}>{room.ir_gender === 'm' ? t.common.male : t.common.female}</Badge>
            </p>
          )}
        </div>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      {!room ? (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">
            <i className="ri-information-line fs-3 d-block mb-2"></i>
            {t.advisorStudents.noRoom}
          </Card.Body>
        </Card>
      ) : (
        <Card className="card-one">
          <Card.Header className="d-flex align-items-center gap-2">
            <span className="fw-semibold">{t.advisorStudents.totalStudents}</span>
            <Badge bg="secondary">{t.common.personCount(room.student_count)}</Badge>
          </Card.Header>
          <Card.Body className="p-0">
            {students.length === 0 ? (
              <p className="text-center text-secondary py-4">{t.advisorStudents.noStudents}</p>
            ) : (
              <Table hover responsive className="mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>{t.advisorStudents.colNo}</th>
                    <th>{t.advisorStudents.colName}</th>
                    <th>{t.advisorStudents.colArabicName}</th>
                    <th className="text-center">{t.advisorStudents.colGender}</th>
                    <th className="text-center">{t.advisorStudents.colRegularClass}</th>
                    <th>{t.advisorStudents.colDorm}</th>
                    <th className="text-center" style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, idx) => (
                    <tr key={s.is_study_id} className={String(s.st_status) === '0' ? 'table-danger' : ''}>
                      <td className="text-secondary align-middle">{idx + 1}</td>
                      <td className="align-middle">{s.st_idnumber}</td>
                      <td className="align-middle">
                        <div className="d-flex align-items-center gap-2">
                          <StudentAvatar photo={s.st_photo} name={s.st_name} />
                          <span>{s.st_name}</span>
                        </div>
                      </td>
                      <td className="align-middle arabic-text">{s.st_arabicname || <span className="text-muted">-</span>}</td>
                      <td className="text-center align-middle">
                        <Badge bg={s.st_gen === 'm' ? 'primary' : 'danger'} className="small text-white">
                          {s.st_gen === 'm' ? t.common.male : t.common.female}
                        </Badge>
                      </td>
                      <td className="text-center align-middle">
                        {formatRegularClass(s.regular_class) || <span className="text-muted">-</span>}
                      </td>
                      <td className="align-middle">
                        {s.dorm_name && s.dorm_id ? (
                          <span
                            className="text-primary"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            title={t.advisorStudents.dormTip}
                            onClick={() => openDormModal(s)}
                          >
                            {s.dorm_name}
                          </span>
                        ) : <span className="text-muted">-</span>}
                      </td>
                      <td className="text-center align-middle">
                        <Link to={`/islamic/advisor/student-info/${s.st_id}`} className="btn btn-outline-primary btn-sm">
                          <i className="ri-contacts-book-2-line me-1"></i>{t.advisorStudents.viewInfo}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={!!dormModal} onHide={() => setDormModal(null)} centered scrollable size="lg">
        <Modal.Header closeButton>
          <Modal.Title as="h6">
            {t.advisorStudents.dormTitle(dormModal?.dorm_name ?? '')}
            {dormModal?.dorm_code && <span className="text-secondary ms-1">({dormModal.dorm_code})</span>}
            {dormModal?.village_name && (
              <span className="text-secondary ms-2">{t.advisorStudents.dormVillage(dormModal.village_name)}</span>
            )}
            <Badge bg="secondary" className="ms-2">{t.common.personCount(dormResidents.length)}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {!dormLoaded ? (
            <div className="text-center py-5 text-secondary">{t.advisorStudents.dormLoading}</div>
          ) : dormResidents.length === 0 ? (
            <div className="text-center py-5 text-secondary">{t.advisorStudents.dormEmpty}</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-secondary">
                <tr>
                  <th className="text-center" style={{ width: 60 }}>{t.advisorStudents.dormColNo}</th>
                  <th className="text-center" style={{ width: 110 }}>{t.advisorStudents.colNo}</th>
                  <th>{t.advisorStudents.colName}</th>
                  <th className="text-center" style={{ width: 110 }}>{t.advisorStudents.dormColClass}</th>
                  <th className="text-center" style={{ width: 110 }}>{t.advisorStudents.dormColIslamic}</th>
                </tr>
              </thead>
              <tbody>
                {dormResidents.map((r, i) => (
                  // ไฮไลต์คนที่กดเข้ามา เพื่อให้เห็นว่าตัวเองอยู่ตรงไหนในรายชื่อหอ
                  <tr key={r.st_id} className={r.st_id === dormModal?.st_id ? 'table-primary' : undefined}>
                    <td className="text-center text-secondary">{i + 1}</td>
                    <td className="text-center">{r.st_idnumber || '-'}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <StudentAvatar photo={r.st_photo} name={r.st_name} />
                        <span className="text-truncate">{r.st_name}</span>
                      </div>
                    </td>
                    <td className="text-center">
                      {r.c_no && r.r_no ? `ม.${r.c_no}/${r.r_no}` : <span className="text-muted">-</span>}
                    </td>
                    <td className="text-center">
                      {r.ic_class ? `${r.ic_class}/${r.isl_room_no}` : <span className="text-muted">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
}
